import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, X } from 'lucide-react';

const PASSWORD_RULES = [
  { test: (p) => p.length >= 12, label: 'Минимум 12 символов' },
  { test: (p) => /[A-ZА-ЯЁ]/.test(p), label: 'Заглавная буква' },
  { test: (p) => /[a-zа-яё]/.test(p), label: 'Строчная буква' },
  { test: (p) => /[0-9]/.test(p), label: 'Цифра' },
  { test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(p), label: 'Спецсимвол (!@#$%^&*...)' },
];

const PasswordStrength = ({ password }) => {
  if (!password) return null;
  return (
    <div className="space-y-1 mt-2">
      {PASSWORD_RULES.map((rule, i) => {
        const passed = rule.test(password);
        return (
          <div key={i} className={`flex items-center gap-1.5 text-xs ${passed ? 'text-green-600' : 'text-gray-400'}`}>
            {passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
            {rule.label}
          </div>
        );
      })}
    </div>
  );
};

// Определяет redirect path по роли пользователя
const getRedirectPath = (user) => {
  const role = user?.role?.toLowerCase();
  if (role === 'goszakup') {
    return '/goszakup';
  }
  return '/dashboard';
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [changeMode, setChangeMode] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const { login, setMustChangePassword, user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password, rememberMe);
      if (data.must_change_password) {
        setChangeMode(true);
        setCurrentPassword(password);
      } else {
        navigate(getRedirectPath(data.user));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка входа. Проверьте данные.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    const allPassed = PASSWORD_RULES.every((r) => r.test(newPassword));
    if (!allPassed) {
      setError('Пароль не соответствует требованиям безопасности');
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setMustChangePassword(false);
      navigate(getRedirectPath(user));
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка смены пароля');
    } finally {
      setLoading(false);
    }
  };

  if (changeMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Смена пароля</CardTitle>
            <CardDescription className="text-center">
              Для безопасности необходимо установить новый пароль
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="newPassword">Новый пароль</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <PasswordStrength password={newPassword} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500">Пароли не совпадают</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !PASSWORD_RULES.every((r) => r.test(newPassword)) || newPassword !== confirmPassword}
              >
                {loading ? 'Сохранение...' : 'Установить пароль'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Центр управления проектами</CardTitle>
          <CardDescription className="text-center">
            Система управления проектами
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Эл. почта</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@logitex.kz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                  disabled={loading}
                />
                <span className="text-sm text-gray-600">Запомнить меня</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">
                Забыли пароль?
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Вход...' : 'Войти'}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p className="text-xs text-gray-400 mb-2">
              Для получения доступа обратитесь к администратору
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
